import { useEffect, useState } from "react";
import WaitlistForm from "./WaitlistForm";
import { getWaitlistStats } from "@/lib/api";

export default function Hero() {
  const [stats, setStats] = useState({ total_signups: 0, slots_remaining: 100, offer_limit: 100 });

  useEffect(() => {
    getWaitlistStats().then(setStats).catch(() => {});
  }, []);

  return (
    <section
      id="waitlist"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-white"
    >
      {/* Background image: light urban scene with buildings and cars */}
      <div
        className="absolute inset-0 -z-10 bg-cover"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1920&q=80&auto=format&fit=crop')",
          backgroundPosition: "right center",
          opacity: 0.28,
        }}
        aria-hidden
      />
      {/* Strong white gradient overlay so image stays subtle & text-side stays clean */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(to right, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.85) 40%, rgba(255,255,255,0.55) 70%, rgba(255,255,255,0.35) 100%), linear-gradient(to bottom, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.90) 100%)",
        }}
        aria-hidden
      />
      {/* Rich ambient color gradient */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 900px 700px at 10% 100%, rgba(95,168,54,0.18), transparent 60%), radial-gradient(ellipse 800px 600px at 100% 10%, rgba(36,48,63,0.10), transparent 55%), radial-gradient(ellipse 500px 400px at 60% 50%, rgba(95,168,54,0.06), transparent 60%)",
        }}
        aria-hidden
      />
      {/* Dotted grid pattern */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.30]"
        style={{
          backgroundImage:
            "radial-gradient(circle at center, rgba(36,48,63,0.14) 1px, transparent 1.5px)",
          backgroundSize: "28px 28px",
          maskImage:
            "radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)",
        }}
        aria-hidden
      />
      {/* Decorative curved accents — hidden on mobile to avoid Safari GPU crash */}
      <div
        className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-[0.10] -z-10 hidden md:block"
        style={{
          background: "conic-gradient(from 220deg, #5FA836, transparent 40%)",
          filter: "blur(50px)",
        }}
        aria-hidden
      />
      <div
        className="absolute -bottom-32 -left-16 w-[520px] h-[520px] rounded-full opacity-[0.12] -z-10 hidden md:block"
        style={{
          background:
            "radial-gradient(circle, rgba(95,168,54,0.35), transparent 65%)",
          filter: "blur(40px)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 -z-10 grain hidden md:block" aria-hidden />

      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-10 pb-24 md:pt-16 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div
              data-testid="coming-soon-badge"
              className="inline-flex items-center gap-2 rounded-full border border-[#5FA836]/30 bg-[#5FA836]/10 px-3 py-1.5"
            >
              <span className="h-2 w-2 rounded-full bg-[#5FA836] pulse-dot" />
              <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
                Launching 1st October 2026
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="mt-7 font-display text-[40px] sm:text-6xl lg:text-[80px] font-black tracking-[-0.04em] leading-[0.98] text-[#24303F]"
            >
              Post a ride.
              <br />
              Or find one
              <br />
              <span className="gradient-text">going your way.</span>
            </h1>

            <div
              data-testid="hero-tagline"
              className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-[#5FA836]/30 bg-[#5FA836]/10 px-4 py-2.5 sm:px-5 sm:py-3"
            >
              <span className="h-2 w-2 rounded-full bg-[#5FA836] pulse-dot shrink-0" />
              <span className="font-display text-[10.5px] sm:text-base uppercase tracking-[0.1em] sm:tracking-[0.18em] text-[#24303F] font-bold">
                Switch co-traveller <span className="text-[#5FA836]">↔</span> trip host in <span className="text-[#5FA836]">10 minutes</span>
              </span>
            </div>

            <p
              data-testid="hero-subhead"
              className="mt-7 max-w-2xl font-body text-base sm:text-lg text-gray-600 leading-relaxed"
            >
              RideMate connects trip hosts and co-travellers heading the same way —
              split the fare, save fuel, ride together. And the only app where
              any rider can become a trip host in under 10 minutes.
            </p>

            <div className="mt-10">
              <WaitlistForm />
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4">
              <Stat label="Already joined" value={stats.total_signups} />
              <Divider />
              <Stat
                label="₹100 slots left"
                value={stats.slots_remaining}
                accent="text-[#5FA836]"
              />
              <Divider />
              <Stat label="Avg savings" value="60%" />
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <div className="float-slow rounded-3xl border border-[#5FA836]/25 bg-white/95 backdrop-blur-md p-7 shadow-[0_30px_80px_-30px_rgba(95,168,54,0.35),0_10px_30px_-15px_rgba(36,48,63,0.15)] relative overflow-hidden">
              <div
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-40"
                style={{ background: "radial-gradient(circle, rgba(95,168,54,0.30), transparent 70%)" }}
                aria-hidden
              />
              <div className="relative">
                <div className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
                  Launch offer
                </div>
                <div className="mt-3 font-display text-5xl font-black leading-none text-[#24303F]">
                  ₹100
                </div>
                <div className="font-body text-gray-600 mt-2 text-sm">
                  cashback on your first ride for the first 100 sign-ups.
                </div>
                <div className="mt-6 h-px w-full bg-[#e5e7eb]" />
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="font-display text-2xl font-bold text-[#24303F]">
                      {stats.slots_remaining}
                      <span className="text-gray-400 font-body text-sm">/{stats.offer_limit}</span>
                    </div>
                    <div className="font-body text-xs text-gray-500 uppercase tracking-wider mt-1">
                      Slots remaining
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 font-display text-[#5FA836] text-sm font-bold uppercase tracking-wider">
                    <span className="h-2 w-2 rounded-full bg-[#5FA836] pulse-dot" />
                    Live
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Stat = ({ label, value, accent = "text-[#24303F]" }) => (
  <div data-testid={`hero-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className={`font-display text-2xl sm:text-3xl font-black ${accent}`}>
      {value}
    </div>
    <div className="font-body text-xs text-gray-500 uppercase tracking-[0.2em] mt-1">
      {label}
    </div>
  </div>
);

const Divider = () => <span className="h-8 w-px bg-[#e5e7eb] hidden sm:inline-block" />;
